"use client";

import { useRef, useState } from "react";
import {
  IconChevronLeft,
  IconChevronRight,
  IconPlayerPlay,
} from "@tabler/icons-react";

// ---------------------------------------------------------------------------
// To update this section, just edit the list below. Any YouTube URL format
// works (youtu.be, watch?v=, /shorts/, /embed/). Entries that aren't valid
// YouTube URLs are skipped automatically.
// ---------------------------------------------------------------------------
export const featuredMediaList = [
  "https://youtu.be/mkWjqAAcyQg?si=MsExP1ea6cPOp-QT",
  "https://youtu.be/OI31Z5iMQnw?si=JWLUV1B3L2CnZwyZ",
  "https://youtu.be/lOA5jRigWq8?si=ZQvr9zscjUSHnEMO",
];

const getYouTubeId = (url: string): string | null => {
  const match = url.match(
    /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([^?&#/]+)/,
  );
  return match ? match[1] : null;
};

const videos = featuredMediaList
  .map((url) => ({ url, id: getYouTubeId(url) }))
  .filter(
    (video): video is { url: string; id: string } => video.id !== null,
  );

const GAP_PX = 16;

export default function FeaturedMedia() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [playingId, setPlayingId] = useState<string | null>(null);

  if (videos.length === 0) return null;

  const stepFor = () => {
    const track = trackRef.current;
    if (!track) return 0;
    const firstCard = track.children[0] as HTMLElement | undefined;
    return firstCard ? firstCard.offsetWidth + GAP_PX : track.clientWidth;
  };

  const goTo = (index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.max(0, Math.min(index, videos.length - 1));
    track.scrollTo({ left: clamped * stepFor(), behavior: "smooth" });
  };

  const handleScroll = () => {
    const track = trackRef.current;
    if (!track) return;
    const step = stepFor();
    if (step <= 0) return;
    setActiveIndex(
      Math.max(
        0,
        Math.min(
          Math.round(track.scrollLeft / step),
          videos.length - 1,
        ),
      ),
    );
  };

  return (
    <section className="flex w-full flex-col items-center px-4 mt-16 lg:mt-24">
      <div className="flex flex-col items-center gap-4">
        <h2 className="text-main-900 text-center font-bold text-[24px] lg:text-[42px]">
          Featured Media
        </h2>
        <p className="text-gray-500 text-center font-normal text-base max-w-xl">
          Watch our latest videos and see what&apos;s happening across our
          communities
        </p>
      </div>

      <div className="w-full lg:w-[1104px] mt-8">
        <div
          ref={trackRef}
          onScroll={handleScroll}
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {videos.map((video, index) => (
            <div
              key={video.id}
              className="snap-start shrink-0 min-w-[85%] sm:min-w-[calc(50%-8px)] lg:min-w-[calc(33.333%-11px)]"
            >
              <div className="relative aspect-video overflow-hidden rounded-2xl bg-slate-900 shadow-sm">
                {playingId === video.id ? (
                  <iframe
                    src={`https://www.youtube.com/embed/${video.id}?autoplay=1&rel=0`}
                    title={`Featured media video ${index + 1}`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="absolute inset-0 h-full w-full"
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => setPlayingId(video.id)}
                    aria-label={`Play featured video ${index + 1}`}
                    className="group absolute inset-0 h-full w-full cursor-pointer"
                  >
                    <img
                      src={`https://i.ytimg.com/vi/${video.id}/hqdefault.jpg`}
                      alt=""
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                    <span className="absolute inset-0 bg-slate-900/20 transition-colors group-hover:bg-slate-900/30" />
                    <span className="absolute inset-0 flex items-center justify-center">
                      <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/90 text-[#19545e] shadow-lg transition-transform duration-300 group-hover:scale-110">
                        <IconPlayerPlay className="h-6 w-6 fill-current" />
                      </span>
                    </span>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {videos.length > 1 && (
          <div className="mt-4 flex items-center justify-center gap-4">
            <button
              type="button"
              onClick={() => goTo(activeIndex - 1)}
              disabled={activeIndex === 0}
              aria-label="Previous videos"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm transition-all hover:border-[#19545e] hover:text-[#19545e] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:text-slate-600 lg:hidden"
            >
              <IconChevronLeft className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-2">
              {videos.map((video, index) => (
                <button
                  key={video.id}
                  type="button"
                  onClick={() => goTo(index)}
                  aria-label={`Go to video ${index + 1}`}
                  className={`h-2 rounded-full transition-all ${
                    index === activeIndex
                      ? "w-6 bg-[#19545e]"
                      : "w-2 bg-slate-300 hover:bg-slate-400"
                  }`}
                />
              ))}
            </div>
            <button
              type="button"
              onClick={() => goTo(activeIndex + 1)}
              disabled={activeIndex === videos.length - 1}
              aria-label="Next videos"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm transition-all hover:border-[#19545e] hover:text-[#19545e] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:text-slate-600 lg:hidden"
            >
              <IconChevronRight className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
